/**
 * Shared account-owner display helpers (issue #414 — "where owner is shown we
 * always show all owners").
 *
 * A joint account has more than one holder. The BFF adapter (`deriveOwners`)
 * joins the account holders with an ampersand ("A & B") on the `Owners` field
 * of `customer/accounts/list` (CCF-80) — the same source the Dashboard tile
 * (#11) and Historic Transactions (#382) read. Some gateway shapes return the
 * owners as an array instead. These helpers normalise BOTH shapes to a single
 * comma-separated display string so every owner-displaying surface lists ALL
 * owners identically, replacing the previously duplicated per-screen
 * `formatOwners` implementations.
 *
 * Purely presentational — the gateway projection is unchanged.
 */

/**
 * The owner value as it can arrive on an account/detail payload: the
 * ampersand-joined string the BFF derives ("A & B"), or an array of names some
 * gateway shapes return (CCF-28). `undefined`/absent ⇒ no owner data.
 */
export type OwnersValue = string | readonly string[] | undefined | null;

/**
 * Normalise an owner value to the individual holder names, trimmed and with
 * empties dropped. Accepts the ampersand-joined string ("A & B"), an array of
 * names, or an absent value (⇒ empty list).
 */
export function ownerNames(owners: OwnersValue): string[] {
  if (Array.isArray(owners)) {
    return owners
      .map((n) => (typeof n === 'string' ? n.trim() : ''))
      .filter((n) => n.length > 0);
  }
  if (typeof owners === 'string') {
    return owners
      .split(/\s*&\s*/)
      .map((n) => n.trim())
      .filter((n) => n.length > 0);
  }
  return [];
}

/**
 * Format the account-holder name(s) for display (issues #11 / #382 / #414):
 * joint holders are rendered comma-separated ("John Smith, Sarah Smith").
 * Returns '' when no owner names are present, so callers can fall back to the
 * verified banner customer name.
 */
export function formatOwners(owners: OwnersValue): string {
  return ownerNames(owners).join(', ');
}
